import { getShouldSourceRecruitersFromAuthService } from '../../../posthog';
import { RemoteConfigUtils } from '../../../utils/RemoteConfigUtils';
import { UserAdapters } from '../adapters';
import { UsersApi } from '../clients';
import { InternalUserRoles } from '../clients/GetInternalUsersByRole';

/**
 * The recruiter list, from whichever source the flag selects.
 *
 * On: the auth-service role query, which maintains itself as recruiters join
 * and leave. Off: the hand-edited `PERMANENT_RECRUITER_IDS` Remote Config list,
 * resolved to user objects through the users-by-ids endpoint.
 *
 * The choice lives here rather than in the hook so it can be a plain `if`.
 * Selecting between two hooks would mean calling both and disabling one --
 * React fixes hook order, so it cannot be an if/else up there -- which reads
 * like a double fetch and needs care to keep the idle one inert. Below the hook
 * boundary none of that applies, and it matches how the DSO manager list picks
 * its source (`getDsoManagers`, which reads its flag outside React too).
 *
 * Both shapes expose `id` and `email`, which is all any consumer reads.
 *
 * @returns {Promise<import('../types/User').IGetInternalUsersResult>}
 */
export const getRecruiters = async () => {
  if (getShouldSourceRecruitersFromAuthService()) {
    const response = await UsersApi.getInternalUsersByRole(InternalUserRoles.Recruiter);

    return UserAdapters.toGetInternalUsersResult(response);
  }

  const recruiterIds = RemoteConfigUtils.getPermanentRecruiterIds();
  // An unset key yields [], and the endpoint would return every user for an
  // empty `userIds`, so this short-circuits rather than asking for the world.
  if (!recruiterIds.length) return { data: [] };

  const response = await UsersApi.getUsersByIds(recruiterIds);

  return UserAdapters.toGetUsersByIdsResult(response);
};
